import { supabase } from '../lib/supabase';

export interface FavoriteDrillStep {
  id: string;
  step_name: string;
  drill_id: string;
  drill_name: string;
  setup?: string;
  execution?: string;
  equipment?: string;
  video_url?: string;
  coaching_points?: string[];
}

export const FavoriteService = {
  async getFavoriteStepIds(userId: string): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('favorite_drill_steps')
        .select('drill_step_id')
        .eq('user_id', userId);

      if (error) throw error;
      return data.map(item => item.drill_step_id);
    } catch (error) {
      console.error('Error fetching favorite step IDs:', error);
      return [];
    }
  },

  async getFavoriteDrillSteps(userId: string): Promise<FavoriteDrillStep[]> {
    try {
      // Get favorite drill steps with basic info
      const { data: favorites, error: favoritesError } = await supabase
        .from('favorite_drill_steps')
        .select('id, step_name, drill_id, drill_name, drill_step_id')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (favoritesError) throw favoritesError;

      console.log('Found favorites:', favorites);

      // Get full step details for each favorite
      const stepsWithDetails: FavoriteDrillStep[] = [];
      
      for (const favorite of favorites) {
        const { data: stepDetails, error: stepError } = await supabase
          .from('drill_steps')
          .select('setup, execution, equipment, video_url')
          .eq('id', favorite.drill_step_id)
          .single();

        if (stepError) {
          console.warn(`Could not fetch details for step ${favorite.drill_step_id}:`, stepError);
          continue;
        }

        // Get coaching points for this step
        const { data: coachingPoints, error: cpError } = await supabase
          .from('drill_coaching_points')
          .select('point, order_index')
          .eq('drill_step_id', favorite.drill_step_id)
          .order('order_index');

        const points = cpError ? [] : coachingPoints.map(cp => cp.point);

        stepsWithDetails.push({
          id: favorite.drill_step_id, // Use the actual drill step ID, not the favorite record ID
          step_name: favorite.step_name,
          drill_id: favorite.drill_id,
          drill_name: favorite.drill_name,
          setup: stepDetails.setup || '',
          execution: stepDetails.execution || '',
          equipment: stepDetails.equipment || '',
          video_url: stepDetails.video_url || '',
          coaching_points: points
        });
      }

      return stepsWithDetails;
    } catch (error) {
      console.error('Error fetching favorite drill steps:', error);
      return [];
    }
  },

  async toggleFavoriteStep(userId: string, stepId: string): Promise<boolean> {
    try {
      // Check if the step is already favorited using maybeSingle to avoid errors
      const { data: existingFavorite, error: checkError } = await supabase
        .from('favorite_drill_steps')
        .select('id')
        .eq('user_id', userId)
        .eq('drill_step_id', stepId)
        .maybeSingle(); // Use maybeSingle() instead of single() to avoid errors when no record found

      // If there's an error, it's a real error (maybeSingle doesn't throw PGRST116)
      if (checkError) {
        console.error('Error checking existing favorite:', checkError);
        throw checkError;
      }

      if (existingFavorite) {
        // Remove from favorites
        const { error } = await supabase
          .from('favorite_drill_steps')
          .delete()
          .eq('id', existingFavorite.id);

        if (error) {
          console.error('Error removing from favorites:', error);
          throw error;
        }

        return false; // Indicates item was removed
      } else {
        // Add to favorites - need to get step details first
        const { data: step, error: stepError } = await supabase
          .from('drill_steps')
          .select('id, name, drill_id')
          .eq('id', stepId)
          .single();

        if (stepError) {
          console.error('Error fetching step details:', stepError);
          throw stepError;
        }

        // Get drill name
        const { data: drill, error: drillError } = await supabase
          .from('drills')
          .select('name')
          .eq('id', step.drill_id)
          .single();

        if (drillError) {
          console.error('Error fetching drill details:', drillError);
          throw drillError;
        }

        // Build the favorite record
        const favoriteRecord = {
          user_id: userId,
          drill_step_id: stepId,
          step_name: step.name,
          drill_id: step.drill_id,
          drill_name: drill.name
        };

        // Add to favorites
        const { error } = await supabase
          .from('favorite_drill_steps')
          .insert(favoriteRecord);

        if (error) {
          console.error('Error inserting favorite:', error);
          throw error;
        }

        return true; // Indicates item was added
      }
    } catch (error) {
      console.error('Error toggling favorite step:', error);
      throw error;
    }
  },

  async getCurrentUserId(): Promise<string> {
    const { data: userData } = await supabase.auth.getUser();
    return userData?.user?.id || 'anonymous';
  }
}; 